import {
  SiteReaderKind,
  type GetSiteReaderDevicesQuery,
} from 'apollo/generated/client-operations';
import { readerLabel, type ReaderDisplayFields } from './readerPresentation';

export type SiteReader = Pick<ReaderDisplayFields, 'kind' | 'health'> & {
  /** The reader device id, which the layout's forms carry. */
  id: string;
  /** The site's reader the layout commands place. */
  readerId: string;
  name: string;
  portLabel: string;
  /** Avigilon no longer lists it for this site; it is offered for placing, marked. */
  missing: boolean;
};

type ReaderRead = Pick<
  GetSiteReaderDevicesQuery['siteIndependentReaders'][number],
  | 'readerId'
  | 'readerDeviceId'
  | 'name'
  | 'missing'
  | 'kind'
  | 'entry'
  | 'health'
>;

/**
 * A Wiegand reader is named by its controller, its port and the entry Alta
 * records it on; an Alta reader by its own name.
 */
const toSiteReader = (
  reader: ReaderRead,
  portLabel: string,
  controllerName?: string,
  portName?: string,
): SiteReader => ({
  id: reader.readerDeviceId,
  readerId: reader.readerId,
  name: readerLabel(reader, controllerName, portName),
  kind: reader.kind,
  health: reader.health,
  missing: reader.missing,
  portLabel: reader.missing
    ? 'Not listed for this site in Avigilon'
    : portLabel,
});

/**
 * Every reader open on the site, by name, once each: those the site records
 * under a controller, those recorded independent, and those with no
 * recorded controller, on a controller's port or on none. The layout places
 * any reader open on the site.
 */
export const siteReadersOf = (
  data: GetSiteReaderDevicesQuery,
): SiteReader[] => {
  const readers = data.siteUnresolvedReaders.map((reader) =>
    toSiteReader(
      reader,
      reader.controller
        ? `${reader.controller.name} · ${reader.controller.portLabel || 'Port'}`
        : 'Not on a port',
      reader.controller?.name,
      reader.controller?.portLabel,
    ),
  );
  readers.push(
    ...data.siteDevices.flatMap((controller) =>
      controller.readers.map((reader) => {
        const port =
          reader.port && (reader.port.label || `Port ${reader.port.position}`);
        return toSiteReader(
          reader,
          port ? `${controller.name} · ${port}` : 'Not on a port',
          controller.name,
          port ?? undefined,
        );
      }),
    ),
  );
  const byDeviceId = new Map(readers.map((reader) => [reader.id, reader]));
  data.siteIndependentReaders.forEach((reader) => {
    const independent = toSiteReader(reader, 'Independent reader');
    const attached = byDeviceId.get(independent.id);
    // Independent ownership does not erase a known physical port.
    if (independent.kind === SiteReaderKind.Wiegand && attached)
      independent.name = attached.name;
    byDeviceId.set(independent.id, independent);
  });
  return [...byDeviceId.values()].sort((a, b) => a.name.localeCompare(b.name));
};
